import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import PageHero from '@/components/shared/PageHero';
import DonationForm from '@/components/forms/DonationForm';
import { useSite } from '@/context/SiteContext';
import {
    Copy,
    CheckCircle2,
    ShieldCheck,
    QrCode,
    Heart,
    ArrowRight,
    Landmark,
    AlertCircle,
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const CopyButton = ({ value, testId }) => {
    const [copied, setCopied] = useState(false);
    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success('Copied to clipboard');
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Couldn't copy — please copy manually.");
        }
    };
    return (
        <Button
            size="sm"
            variant="outline"
            onClick={doCopy}
            className="h-8"
            data-testid={testId}
        >
            {copied ? (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            ) : (
                <Copy className="h-3.5 w-3.5 mr-1" />
            )}
            {copied ? 'Copied' : 'Copy'}
        </Button>
    );
};

const BankRow = ({ label, value, testId }) => (
    <div
        className="p-3 rounded-lg border border-border bg-[hsl(var(--secondary))]/50"
        data-testid={testId ? `bank-row-${testId}` : undefined}
    >
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-sm text-foreground truncate" title={value}>
                {value}
            </div>
            <CopyButton
                value={value}
                testId={testId ? `copy-${testId}` : undefined}
            />
        </div>
    </div>
);

export default function DonatePage() {
    const { settings } = useSite();
    useSEO({
        title: 'Donate — Support Us',
        description:
            'Support VVVSI Trust with a donation. Direct bank transfer, UPI, one-time / monthly / corporate CSR / in-kind. All donations are tax-exempt under Section 80G. Registered under 80G, 12A & CSR-1.',
    });

    const bank = settings?.bank_details || {};
    const upi = settings?.upi_id;
    const upiQR = settings?.upi_qr_url;

    // Detect if the primary bank details are filled
    const hasBankDetails = !!(
        (bank.account_name || bank.bank_name) &&
        bank.account_number &&
        bank.ifsc
    );

    return (
        <div data-testid="donate-page">
            <PageHero
                eyebrow="Support Us"
                title="Your contribution funds real programs on the ground."
                subtitle="Registered under 80G, 12A & CSR-1. All donations are tax-exempt in India."
            />
            <section className="section-pad">
                <div className="container-page grid lg:grid-cols-12 gap-8">
                    {/* Payment info */}
                    <div className="lg:col-span-7 space-y-6">
                        <Card className="bg-card border-border" data-testid="donation-bank-details">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-3">
                                    <Landmark className="h-4 w-4" />
                                    <span className="text-xs font-semibold uppercase tracking-widest">
                                        Bank Transfer
                                    </span>
                                </div>
                                <h2 className="font-display text-2xl font-semibold mb-4">
                                    Direct Bank Deposit
                                </h2>

                                {hasBankDetails ? (
                                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                                        {bank.account_name && (
                                            <BankRow
                                                label="Account Name"
                                                value={bank.account_name}
                                                testId="account-name"
                                            />
                                        )}
                                        {bank.account_number && (
                                            <BankRow
                                                label="Account Number"
                                                value={bank.account_number}
                                                testId="account-number"
                                            />
                                        )}
                                        {bank.bank_name && (
                                            <BankRow
                                                label="Bank Name"
                                                value={bank.bank_name}
                                                testId="bank-name"
                                            />
                                        )}
                                        {bank.branch && (
                                            <BankRow
                                                label="Branch"
                                                value={bank.branch}
                                                testId="branch"
                                            />
                                        )}
                                        {bank.ifsc && (
                                            <BankRow
                                                label="IFSC Code"
                                                value={bank.ifsc}
                                                testId="ifsc"
                                            />
                                        )}
                                        {bank.swift && (
                                            <BankRow
                                                label="SWIFT (International)"
                                                value={bank.swift}
                                                testId="swift"
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className="flex items-start gap-3 rounded-lg border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-4"
                                        data-testid="bank-empty-state"
                                    >
                                        <AlertCircle className="h-5 w-5 text-[hsl(30_90%_28%)] mt-0.5 shrink-0" />
                                        <div className="text-sm text-foreground/85">
                                            <div className="font-semibold text-foreground mb-1">
                                                Bank details are being updated.
                                            </div>
                                            <div className="text-muted-foreground">
                                                Please contact us on{' '}
                                                <a
                                                    href={`tel:${settings?.contact_phone}`}
                                                    className="font-semibold text-[hsl(var(--primary))] hover:underline"
                                                >
                                                    {settings?.contact_phone}
                                                </a>{' '}
                                                or{' '}
                                                <a
                                                    href={`mailto:${settings?.contact_email}`}
                                                    className="font-semibold text-[hsl(var(--primary))] hover:underline"
                                                >
                                                    {settings?.contact_email}
                                                </a>{' '}
                                                for account details.
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {(upi || upiQR) && (
                            <Card className="bg-card border-border">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-3">
                                        <QrCode className="h-4 w-4" />
                                        <span className="text-xs font-semibold uppercase tracking-widest">
                                            UPI Payment
                                        </span>
                                    </div>
                                    <h2 className="font-display text-2xl font-semibold mb-4">
                                        Scan & Pay via UPI
                                    </h2>
                                    <div className="flex flex-col sm:flex-row items-start gap-6">
                                        {upiQR && (
                                            <div className="shrink-0 p-3 bg-white border border-border rounded-xl">
                                                <img
                                                    src={upiQR}
                                                    alt="UPI QR"
                                                    className="w-40 h-40 object-contain"
                                                    data-testid="donation-upi-qr"
                                                />
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            {upi && (
                                                <>
                                                    <div className="text-xs text-muted-foreground mb-1">
                                                        UPI ID
                                                    </div>
                                                    <div className="flex items-center gap-2 mb-4">
                                                        <div
                                                            className="font-display font-semibold text-lg text-foreground"
                                                            data-testid="donation-upi-id"
                                                        >
                                                            {upi}
                                                        </div>
                                                        <CopyButton
                                                            value={upi}
                                                            testId="donation-copy-upi-button"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                Any UPI app (Google Pay, PhonePe, Paytm, BHIM) works.
                                                After paying, please share the transaction reference
                                                using the form on the right so we can send you a
                                                receipt for tax purposes.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <div className="flex items-start gap-3 rounded-lg border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/10 p-4">
                            <ShieldCheck className="h-5 w-5 text-[hsl(30_90%_28%)] mt-0.5" />
                            <div className="text-sm text-foreground/85">
                                <div className="font-semibold">80G, 12A & CSR-1 Registered</div>
                                <div className="text-muted-foreground">
                                    Donations are eligible for tax deduction under Section 80G of
                                    the Income Tax Act.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inquiry Form */}
                    <div className="lg:col-span-5">
                        <Card className="bg-card border-border sticky top-24">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-3">
                                    <ArrowRight className="h-4 w-4" />
                                    <span className="text-xs font-semibold uppercase tracking-widest">
                                        Get in Touch
                                    </span>
                                </div>
                                <h2 className="font-display text-2xl font-semibold mb-4">
                                    Donation Inquiry
                                </h2>
                                <DonationForm />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}
